import { request } from './client'
import type {
  ProviderCredential,
  ProviderEvent,
  Integration,
  IntegrationTier,
  IntegrationTicket,
  TicketStats,
  SyncLog,
  ValidateCredentialsRequest,
  ValidateCredentialsResponse,
  ProviderEventsRequest,
  CreateCredentialRequest,
  CreateIntegrationRequest,
  UpdateTierRequest,
  IntegrationsParams,
  TicketsParams,
} from './provider-types'
import type { PaginatedResponse } from './types'

type ParamsObject = Record<string, string | number | boolean | undefined | null>

export const providerApiClient = {
  // --- Credentials ---

  validateCredentials: (token: string, data: ValidateCredentialsRequest): Promise<ValidateCredentialsResponse> => {
    return request('/dashboard-integration/validate-credentials/', { method: 'POST', token, body: data })
  },

  listCredentials: (token: string, params?: { organization?: number; provider_type?: string }): Promise<PaginatedResponse<ProviderCredential>> => {
    return request('/dashboard-provider-credential/', { token, params: params as ParamsObject })
  },

  createCredential: (token: string, data: CreateCredentialRequest): Promise<ProviderCredential> => {
    return request('/dashboard-provider-credential/', { method: 'POST', token, body: data })
  },

  deleteCredential: (token: string, id: number): Promise<void> => {
    return request(`/dashboard-provider-credential/${id}/`, { method: 'DELETE', token })
  },

  // --- Provider Discovery ---

  getProviderEvents: async (token: string, data: ProviderEventsRequest): Promise<ProviderEvent[]> => {
    const response = await request<{ events: ProviderEvent[] }>('/dashboard-integration/provider-events/', { method: 'POST', token, body: data })
    return response.events
  },

  // --- Integrations ---

  listIntegrations: (token: string, params?: IntegrationsParams): Promise<PaginatedResponse<Integration>> => {
    return request('/dashboard-integration/', { token, params: params as ParamsObject })
  },

  getIntegration: (token: string, id: number): Promise<Integration> => {
    return request(`/dashboard-integration/${id}/`, { token })
  },

  createIntegration: (token: string, data: CreateIntegrationRequest): Promise<Integration> => {
    return request('/dashboard-integration/', { method: 'POST', token, body: data })
  },

  updateIntegration: (token: string, id: number, data: Partial<Integration>): Promise<Integration> => {
    return request(`/dashboard-integration/${id}/`, { method: 'PATCH', token, body: data })
  },

  deleteIntegration: (token: string, id: number): Promise<void> => {
    return request(`/dashboard-integration/${id}/`, { method: 'DELETE', token })
  },

  // --- Tiers ---

  getIntegrationTiers: (token: string, integrationId: number): Promise<IntegrationTier[]> => {
    return request(`/dashboard-integration/${integrationId}/tiers/`, { token })
  },

  syncTiers: (token: string, integrationId: number): Promise<IntegrationTier[]> => {
    return request(`/dashboard-integration/${integrationId}/sync-tiers/`, { method: 'POST', token })
  },

  updateTier: (token: string, integrationId: number, tierId: number, data: UpdateTierRequest): Promise<IntegrationTier> => {
    return request(`/dashboard-integration/${integrationId}/tiers/${tierId}/`, { method: 'PATCH', token, body: data })
  },

  // --- Tickets ---

  getTickets: (token: string, integrationId: number, params?: TicketsParams): Promise<PaginatedResponse<IntegrationTicket>> => {
    return request(`/dashboard-integration/${integrationId}/tickets/`, { token, params: params as ParamsObject })
  },

  getTicketStats: (token: string, integrationId: number): Promise<TicketStats> => {
    return request(`/dashboard-integration/${integrationId}/tickets/stats/`, { token })
  },

  matchTicket: (token: string, integrationId: number, ticketId: number): Promise<IntegrationTicket> => {
    return request(`/dashboard-integration/${integrationId}/tickets/${ticketId}/match/`, { method: 'POST', token })
  },

  creditTickets: (token: string, integrationId: number, ticketIds: number[]): Promise<{ credited: number }> => {
    return request(`/dashboard-integration/${integrationId}/tickets/credit/`, { method: 'POST', token, body: { ticket_ids: ticketIds } })
  },

  // --- Sync ---

  syncIntegration: (token: string, integrationId: number): Promise<SyncLog> => {
    return request(`/dashboard-integration/${integrationId}/sync/`, { method: 'POST', token })
  },

  getSyncLogs: (token: string, integrationId: number): Promise<PaginatedResponse<SyncLog>> => {
    return request(`/dashboard-integration/${integrationId}/sync-logs/`, { token })
  },
}
